import { cn } from "../../../lib/utils";

/**
 * Transparent host-DOM plate mounted ABOVE an HTML card's iframe whenever the
 * iframe does not own the pointer. This is the canvas-panning fix:
 * `pointer-events: none` on the iframe is only a main-thread hit-test
 * primitive — wheel/trackpad input can still be routed to the opaque-origin
 * frame's own (out-of-process) document, where it can never bubble back to
 * React Flow's pane listeners. No `nowheel`/`nopan` class can compensate,
 * because those are only consulted by a listener that never fires. With the
 * shield as the topmost hit target, every wheel/pointer event stays in the
 * host document, so pan/zoom/drag/marquee behave as over any other node.
 *
 * Same overlay idiom as TextNoteNode/TextLabelNode (overlay removed while
 * editing). Deliberately carries NO handlers and NO `nodrag`/`nowheel`
 * classes: events bubble to the card root and React Flow, which is the point.
 *
 * `hintLabel` paints nothing here — the capability-aware affordance lives
 * in the node chrome's foot, outside the block box, so it never covers
 * content. The prop stays because the shield still uses it to decide whether
 * the plate advertises a click target at all.
 */
export function HtmlBlockShield({
  hintLabel,
  cursor = "default",
  open = false,
}: {
  hintLabel?: string | null;
  /** What a click under the pointer would do, from the runtime's hover
   *  answer — the plate wears the matching cursor so the gesture is
   *  announced before it happens. */
  cursor?: "default" | "pointer" | "text";
  /** The pointer rests over a control: the plate goes pointer-transparent
   *  so the control is native (its own hover state, drags, wheel), while
   *  staying mounted for the moment the pointer moves back onto content. */
  open?: boolean;
}) {
  return (
    <div
      data-cohesive-block-shield=""
      className={cn(
        "absolute inset-0 z-[1]",
        open && "pointer-events-none",
        !open && hintLabel && cursor === "text" && "cursor-text",
      )}
    />
  );
}
